/**
 * ADR-166 — Employment Episode Discrepancies.
 *
 * Contracts that cannot be right, one row per finding: what is wrong, the
 * contracts involved, and how it is fixed. Read-only — the Fix column is plain
 * text; the individual's Contracts / Position history links in the first
 * column lead to the screens (owner, 2026-10-09). No buttons change data.
 *
 * Laid out like Reports → Ingestion Sync Runs and Overlapping Position Legs:
 * the same filter bar, the "N … found" count, the same table and Load More.
 * Filters, the individual searched, the rows shown and the scroll position are
 * restored on return from an individual's page.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import {
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../styles';
import { HeaderStats, HeaderStat } from '../EmploymentChangeLogReport/styles';
import {
  FilterBar,
  TableWrapper,
  TwoLine,
  TwoLinePrimary,
  TwoLineSecondary
} from '../DirectManagerDiscrepancies/styles';
import { LinkButton } from '../IngestionSyncRuns/styles';
// The Bench Candidates Load More button (owner, 2026-10-09).
import { LoadMoreButton } from '../BenchCandidates/styles';
import { getChipSx } from '../../../constants/chipPalette';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import EmployeeSearch from '../../common/EmployeeSearch';
import ClusterSelector from '../../common/ClusterSelector';
import { CLUSTER_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { REGION_STRUCTURE_TYPE_CODE } from '../../../constants/orgStructureTypes';
import { EmployeeSearchProvider } from '../../common/EmployeeSearch/contexts/EmployeeSearchProvider';
import { employmentApi } from '../../../services/api';
import {
  CATEGORY_OPTIONS,
  PAGE_SIZE,
  SEVERITY_CHIP,
  SEVERITY_LABELS,
  SEVERITY_OPTIONS,
  checkOptions,
  contractLabel,
  filterRows
} from '../../../utils/employmentEpisodeDiscrepancies';

const REPORT_PATH = '/reports/employment-episode-discrepancies';
const DEFAULT_FILTERS = { category: '', check: '', severity: '', regionId: '' };

const SingleSelect = ({ id, label, value, options, onChange, minWidth = 160 }) => (
  <FormControl size="small" sx={{ minWidth }}>
    <InputLabel id={id}>{label}</InputLabel>
    <Select labelId={id} label={label} value={value || ''} onChange={(event) => onChange(event.target.value)}>
      <MenuItem value=""><em>All</em></MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

const EmploymentEpisodeDiscrepanciesReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restored = location.state?.discrepancyState;

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...(restored?.filters || {}) });
  const [selectedEmployee, setSelectedEmployee] = useState(restored?.selectedEmployee || null);
  const [visible, setVisible] = useState(restored?.visible || PAGE_SIZE);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      setReport(await employmentApi.getEmploymentEpisodeDiscrepancies());
    } catch (err) {
      setLoadError(err.message || 'Failed to load the report');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Back on the row the user left from, once the rows are on screen.
  useEffect(() => {
    if (!isLoading && report && restored?.scrollY) {
      requestAnimationFrame(() => window.scrollTo(0, restored.scrollY));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, report]);

  const rows = useMemo(
    () => filterRows(report?.rows, { ...filters, employeeId: selectedEmployee?.id || '' }),
    [report, filters, selectedEmployee]
  );
  const checks = useMemo(() => checkOptions(report), [report]);

  const setFilter = (next) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setVisible(PAGE_SIZE);
  };

  const openIndividual = (path) => navigate(path, {
    state: {
      returnTo: REPORT_PATH,
      returnLabel: 'Employment Episode Discrepancies',
      discrepancyState: { filters, selectedEmployee, visible, scrollY: window.scrollY }
    }
  });

  const summary = report?.summary;
  const pageRows = rows.slice(0, visible);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Employment Episode Discrepancies</Title>
        </HeaderLeft>
        <HeaderStats>
          {isLoading && !summary ? <CircularProgress size={20} /> : summary && (
            <>
              <HeaderStat>
                <div className="top"><span className="label">People</span><span className="value">{summary.people}</span></div>
              </HeaderStat>
              <HeaderStat $alarm $clear={summary.bySeverity.error === 0}>
                <div className="top"><span className="label">Errors</span><span className="value">{summary.bySeverity.error}</span></div>
                <span className="sub">Cannot be right</span>
              </HeaderStat>
              <HeaderStat>
                <div className="top"><span className="label">Warnings</span><span className="value">{summary.bySeverity.warning}</span></div>
                <span className="sub">Very likely wrong</span>
              </HeaderStat>
              <HeaderStat>
                <div className="top"><span className="label">Info</span><span className="value">{summary.bySeverity.info}</span></div>
                <span className="sub">Worth a look</span>
              </HeaderStat>
            </>
          )}
        </HeaderStats>
      </Header>

      <Content>
        <MainSection>
          <FilterBar>
            <SingleSelect id="eed-category-label" label="Category" value={filters.category} options={CATEGORY_OPTIONS}
              onChange={(value) => setFilter({ category: value })} />
            <SingleSelect id="eed-check-label" label="Check" value={filters.check} options={checks}
              onChange={(value) => setFilter({ check: value })} minWidth={280} />
            <SingleSelect id="eed-severity-label" label="Severity" value={filters.severity} options={SEVERITY_OPTIONS}
              onChange={(value) => setFilter({ severity: value })} />
            {/* The individual's Region — the same picker as the Individuals
                Repository "Regions" filter (ADR-119 amendment 1). */}
            <ClusterSelector
              value={filters.regionId}
              onChange={(value) => setFilter({ regionId: value || '' })}
              label="Region"
              allowSelectAll={true}
              allOptionLabel="All Regions"
              allOptionValue=""
              structureTypeCodes={[REGION_STRUCTURE_TYPE_CODE]}
              returnValue={CLUSTER_RETURN_TYPES.ID}
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />
            <EmployeeSearchProvider instanceId="employment-episode-discrepancies-search">
              <EmployeeSearch
                value={selectedEmployee}
                onChange={(employee) => { setSelectedEmployee(employee); setVisible(PAGE_SIZE); }}
                label="Individual"
                placeholder="Search individuals..."
                width={220}
                height={40}
                hideLabel
              />
            </EmployeeSearchProvider>
          </FilterBar>

          <ResultsCount count={rows.length} singular="discrepancy" plural="discrepancies" />

          {isLoading && <LoadingSpinner />}
          {!isLoading && loadError && (
            <EmptyState title="Failed to load the report" description={loadError} />
          )}
          {!isLoading && !loadError && rows.length === 0 && (
            <EmptyState
              title={report?.rows?.length ? 'No discrepancies match the filters' : 'No discrepancies found'}
              description={report?.rows?.length ? 'Clear a filter to see more.' : 'Every contract passes every check.'}
            />
          )}
          {!isLoading && !loadError && rows.length > 0 && (
            <>
              <TableWrapper>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Individual</TableCell>
                        <TableCell>Check</TableCell>
                        <TableCell>What is wrong</TableCell>
                        <TableCell>Contracts involved</TableCell>
                        <TableCell>Fix</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map((r, index) => (
                        <TableRow key={`${r.check}-${r.employeeId}-${index}`} hover sx={{ verticalAlign: 'top' }}>
                          <TableCell>
                            <TwoLine>
                              <TwoLinePrimary>{r.fullName || '—'}</TwoLinePrimary>
                              {r.regionName && <TwoLineSecondary>{r.regionName}</TwoLineSecondary>}
                              <TwoLineSecondary>
                                <LinkButton type="button" onClick={() => openIndividual(`/employees/${r.employeeId}/edit?tab=EMPLOYMENT_EPISODES`)}>Contracts</LinkButton>
                                {' · '}
                                <LinkButton type="button" onClick={() => openIndividual(`/employees/${r.employeeId}/positions`)}>Position history</LinkButton>
                              </TwoLineSecondary>
                            </TwoLine>
                          </TableCell>
                          <TableCell>
                            <TwoLine>
                              <span>
                                <Chip size="small" label={SEVERITY_LABELS[r.severity]} sx={getChipSx(SEVERITY_CHIP[r.severity])} />
                              </span>
                              <TwoLinePrimary>{r.title}</TwoLinePrimary>
                              <TwoLineSecondary>{r.categoryLabel}</TwoLineSecondary>
                            </TwoLine>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 420 }}>{r.sentence}</TableCell>
                          <TableCell>
                            <TwoLine>
                              {(r.contracts || []).length === 0 && <TwoLineSecondary>—</TwoLineSecondary>}
                              {(r.contracts || []).map((c) => (
                                <TwoLineSecondary key={c.id} style={c.primary ? { fontWeight: 600 } : undefined}>{contractLabel(c)}</TwoLineSecondary>
                              ))}
                            </TwoLine>
                          </TableCell>
                          <TableCell>
                            {/* Muted colour, normal size (owner, 2026-10-09). */}
                            <TwoLineSecondary style={{ fontSize: 'inherit' }}>{r.fix?.label || '—'}</TwoLineSecondary>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableWrapper>
              {rows.length > visible && (
                <LoadMoreButton onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                  Load More
                </LoadMoreButton>
              )}
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default EmploymentEpisodeDiscrepanciesReport;
